import type { RecentSearchItem } from '../search.types';

const RECENT_SEARCHES_KEY = 'AUTOCOMPLETE_RECENT_SEARCHES:instantsearch';
const RECENT_SEARCHES_TEST_KEY = '__AUTOCOMPLETE_RECENT_SEARCHES_PLUGIN_TEST_KEY__';
const RECENT_SEARCHES_LIMIT = 3;

type RawRecentSearch = {
    id: string;
    label: string;
    category?: string;
};

type RecentSearchInput = {
    label: string;
    category?: string;
};

export type RecentSearches = {
    add: (item: RecentSearchInput) => void;
    list: (query: string) => RecentSearchItem[];
};

export function createRecentSearches(): RecentSearches {
    let supportedStorage: Storage | null | undefined;

    const getSupportedStorage = () => {
        if (supportedStorage !== undefined) return supportedStorage;

        try {
            window.localStorage.setItem(RECENT_SEARCHES_TEST_KEY, '');
            window.localStorage.removeItem(RECENT_SEARCHES_TEST_KEY);
            supportedStorage = window.localStorage;
        } catch {
            supportedStorage = null;
        }

        return supportedStorage;
    };

    return {
        add(item) {
            const storage = getSupportedStorage();
            if (!storage) return;

            const storedItem: RawRecentSearch = { id: item.label, ...item };
            const firstStoredItems = storage.getItem(RECENT_SEARCHES_KEY);
            const firstItems: RawRecentSearch[] = firstStoredItems ? JSON.parse(firstStoredItems) : [];
            storage.setItem(
                RECENT_SEARCHES_KEY,
                JSON.stringify(firstItems.filter(existingItem => existingItem.id !== storedItem.id))
            );

            const secondStoredItems = storage.getItem(RECENT_SEARCHES_KEY);
            const secondItems: RawRecentSearch[] = secondStoredItems ? JSON.parse(secondStoredItems) : [];
            storage.setItem(RECENT_SEARCHES_KEY, JSON.stringify([storedItem, ...secondItems]));
        },
        list(query) {
            const storage = getSupportedStorage();
            if (!storage) return [];

            const items = storage.getItem(RECENT_SEARCHES_KEY);
            const storedItems: RawRecentSearch[] = items ? JSON.parse(items) : [];
            const normalizedQuery = query.toLowerCase();

            return storedItems
                .filter(item => !query || item.label.toLowerCase().includes(normalizedQuery))
                .slice(0, RECENT_SEARCHES_LIMIT)
                .map(item => ({
                    kind: 'recent',
                    id: item.id,
                    label: item.label,
                    category: item.category,
                    highlight: {
                        matchedWords: [],
                        matchLevel: 'none',
                    },
                }));
        },
    };
}
